/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import { cn } from "@/components/podkit/lib/cn";
import { type FC, type PropsWithChildren } from "react";
import { Slot } from "@radix-ui/react-slot";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";

type HeadingProps = {
    id?: string;
    className?: string;
    asChild?: boolean;
} & PropsWithTestId &
    PropsWithChildren;

export const Heading1: FC<HeadingProps> = ({ id, className, children, asChild, ...props }) => {
    const Comp = asChild ? Slot : "h1";

    return (
        <Comp
            id={id}
            className={cn("truncate text-2xl font-semibold tracking-[-0.2px] text-content-primary", className)}
            {...props}
        >
            {children}
        </Comp>
    );
};

export const Heading2: FC<HeadingProps> = ({ id, className, children, asChild, ...props }) => {
    const Comp = asChild ? Slot : "h2";

    return (
        <Comp id={id} className={cn("text-xl font-bold tracking-[-0.2px] text-content-primary", className)} {...props}>
            {children}
        </Comp>
    );
};

export const Heading3: FC<HeadingProps> = ({ id, className, children, asChild, ...props }) => {
    const Comp = asChild ? Slot : "h3";

    return (
        <Comp id={id} className={cn("text-lg font-bold text-content-primary", className)} {...props}>
            {children}
        </Comp>
    );
};

export const Heading4: FC<HeadingProps> = ({ id, className, children, asChild, ...props }) => {
    const Comp = asChild ? Slot : "h4";

    return (
        <Comp id={id} className={cn("text-base font-bold text-content-primary", className)} {...props}>
            {children}
        </Comp>
    );
};

/**
 * Intended to be placed beneath a heading to provide more context
 */
export const Subheading: FC<HeadingProps> = ({ id, className, children, asChild, ...props }) => {
    const Comp = asChild ? Slot : "p";

    return (
        <Comp id={id} className={cn("text-base text-content-secondary", className)} {...props}>
            {children}
        </Comp>
    );
};
